import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';

interface ProductSlugMapping {
  [slug: string]: number; // slug -> variantId
}

interface ProductState {
  slugToVariantId: ProductSlugMapping;
}

type ProductAction =
  | { type: 'SET_SLUG_MAPPING'; payload: { slug: string; variantId: number } }
  | { type: 'SET_BULK_MAPPINGS'; payload: ProductSlugMapping };

const initialState: ProductState = {
  slugToVariantId: {},
};

const productReducer = (state: ProductState, action: ProductAction): ProductState => {
  switch (action.type) {
    case 'SET_SLUG_MAPPING': {
      // Only update if the mapping doesn't exist or is different
      if (state.slugToVariantId[action.payload.slug] === action.payload.variantId) {
        return state; // No change needed
      }
      return {
        ...state,
        slugToVariantId: {
          ...state.slugToVariantId,
          [action.payload.slug]: action.payload.variantId,
        },
      };
    }
    case 'SET_BULK_MAPPINGS': {
      return {
        ...state,
        slugToVariantId: {
          ...state.slugToVariantId,
          ...action.payload,
        },
      };
    }
    default:
      return state;
  }
};

interface ProductContextType extends ProductState {
  setSlugMapping: (slug: string, variantId: number) => void;
  setBulkMappings: (mappings: ProductSlugMapping) => void;
  getVariantIdFromSlug: (slug: string) => number | null;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProduct = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
};

interface ProductProviderProps {
  children: ReactNode;
}

export const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(productReducer, initialState);

  const setSlugMapping = useCallback((slug: string, variantId: number) => {
    dispatch({ type: 'SET_SLUG_MAPPING', payload: { slug, variantId } });
  }, []);

  const setBulkMappings = useCallback((mappings: ProductSlugMapping) => {
    dispatch({ type: 'SET_BULK_MAPPINGS', payload: mappings });
  }, []);

  const getVariantIdFromSlug = useCallback((slug: string): number | null => {
    // First try to get from context
    const variantId = state.slugToVariantId[slug];
    if (variantId) {
      return variantId;
    }
    
    // Fallback: extract from slug format (variantId-slug)
    // This handles direct navigation/bookmarks
    const match = slug.match(/^(\d+)-/);
    if (match) {
      const extractedId = parseInt(match[1], 10);
      return isNaN(extractedId) ? null : extractedId;
    }
    
    return null;
  }, [state.slugToVariantId]);

  const value: ProductContextType = {
    ...state,
    setSlugMapping,
    setBulkMappings,
    getVariantIdFromSlug,
  };

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

